import { TRACKER_GROUP_KEY } from '@shared/flow/tracker';
import { MAX_GROUP_LEVELS } from '@shared/flow/view';
import type { FlowTableConfig } from '../types';

/**
 * Pure grouping helpers shared by `FlowTable` and the V2 `LedgerTable`
 * (plan 2026-09-27-007 D5/D9/U1). Glaze-free and React-free by design.
 */

/**
 * Group-path key delimiter (D7). Collapse state and per-level counts are
 * keyed by the joined tuple of a row's stringified group values up to and
 * including some level — a human-readable delimiter like `,` could collide
 * with a real group value, so this uses a NUL character, which cannot
 * appear in any string a rep can type or that Terra's data can carry.
 */
export const GROUP_PATH_SEP = '\u0000';

/** Group-header label for a cell value — primitives only (rows are wire-flat). */
export function groupValueOf(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '—';
}

/** Pure reorder helper (qa-batch5 D-D) — moves the level at `from` to `to`,
 *  shifting the rest to close the gap. `from === to` (or either index out of
 *  range) is a no-op that returns the SAME array reference, not a copy. */
export function moveLevel(levels: string[], from: number, to: number): string[] {
  if (from === to || from < 0 || to < 0 || from >= levels.length || to >= levels.length) return levels;
  const next = [...levels];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function restoreHiddenGroupBy(working: readonly string[], hidden: readonly string[], next: readonly string[]): string[] {
  const restored = [...next];
  working.forEach((key, index) => {
    if (hidden.includes(key) && !next.includes(key)) restored.splice(Math.min(index, restored.length), 0, key);
  });
  return restored;
}

/** The three pieces a surface needs when it hides some of its `working` group
 *  levels (`hidden`) from the Group menu: the levels the menu shows (`shown`), its cap
 *  `maxLevels` (shrunk by the hidden levels so a saved list stays within
 *  `MAX_GROUP_LEVELS`), and `restore`, which folds the menu's `next` list back
 *  over `working` by re-inserting each hidden level `next` lacks at its working
 *  index (clamped to the end), so an edit does not drop what the full view
 *  still groups by. */
export function splitHiddenGroupBy(working: readonly string[], hidden: readonly string[]) {
  const shown = working.filter(k => !hidden.includes(k));
  return {
    shown,
    maxLevels: MAX_GROUP_LEVELS - (working.length - shown.length),
    restore: (next: readonly string[]) => restoreHiddenGroupBy(working, hidden, next),
  };
}

/** A group header node. `pathKey` = `path.join(GROUP_PATH_SEP)` — the SAME
 *  key FlowTable's collapse overrides and per-level counts use. */
export interface GroupNode<Row> {
  kind: 'group';
  /** 0-based nesting level (index into the `levels` tuple). */
  level: number;
  /** The level's group key (column key), e.g. `'region'`. */
  key: string;
  /** Stringified group value — also the header label. */
  value: string;
  /** Every group value from level 0 through this node's level. */
  path: string[];
  pathKey: string;
  /** Rows under this node, at any depth. */
  count: number;
  children: GroupTreeNode<Row>[];
}

export interface RowNode<Row> {
  kind: 'row';
  row: Row;
}

export type GroupTreeNode<Row> = GroupNode<Row> | RowNode<Row>;

/**
 * Bucket rows into a nested group tree (modelled on hb `GroupedTable.tsx`
 * `buildTree`): at each level a `Map` keyed by the row's value, so buckets
 * come out in FIRST-APPEARANCE order and non-contiguous equal values land
 * in one group. Over server-ordered rows (Terra's case) this equals
 * FlowTable's adjacency-boundary headers, and it also tolerates a
 * `config.groupValue` whose derived value isn't contiguous (D9).
 *
 * `valueOf` is the caller's `groupValueFor` (FlowTable:
 * `groupValueOf(config.groupValue?.(row, key) ?? row[key])`). An empty value
 * is already `—` by the time it gets here — `valueOf` (i.e. `groupValueOf`)
 * is what maps null/empty to that label; a whitespace-only string is a REAL
 * value (master `groupValueOf` parity, adv 18) and buckets on its own.
 */
export function buildGroupTree<Row>(
  rows: readonly Row[],
  levels: readonly string[],
  valueOf: (row: Row, key: string) => string,
): GroupTreeNode<Row>[] {
  return buildLevel(rows, levels, valueOf, 0, []);
}

function buildLevel<Row>(
  rows: readonly Row[],
  levels: readonly string[],
  valueOf: (row: Row, key: string) => string,
  level: number,
  prefix: string[],
): GroupTreeNode<Row>[] {
  if (level >= levels.length) return rows.map(row => ({ kind: 'row', row }));
  const key = levels[level];
  const buckets = new Map<string, Row[]>();
  for (const row of rows) {
    const value = valueOf(row, key);
    const bucket = buckets.get(value);
    if (bucket) bucket.push(row);
    else buckets.set(value, [row]);
  }
  return Array.from(buckets, ([value, bucketRows]): GroupNode<Row> => {
    const path = [...prefix, value];
    return {
      kind: 'group',
      level,
      key,
      value,
      path,
      pathKey: path.join(GROUP_PATH_SEP),
      count: bucketRows.length,
      children: buildLevel(bucketRows, levels, valueOf, level + 1, path),
    };
  });
}

/**
 * Every row id under a group node, at any depth, in render order — collapse
 * is ignored (a collapsed group's hidden rows are still its rows).
 * Reads the tree, never rendered rows, so virtualized/collapsed rows count.
 */
export function collectGroupRowIds<Row>(
  node: GroupNode<Row>,
  rowKeyOf: (row: Row) => string,
): string[] {
  const out: string[] = [];
  const walk = (children: readonly GroupTreeNode<Row>[]) => {
    for (const child of children) {
      if (child.kind === 'group') walk(child.children);
      else out.push(rowKeyOf(child.row));
    }
  };
  walk(node.children);
  return out;
}

/**
 * Flatten a group tree into render order (hb `flattenVisible`): every group
 * header is emitted, and a collapsed group's descendants — headers AND rows
 * at every deeper level — are skipped. `isCollapsed` gets the node's
 * `(pathKey, level, value)` so a caller can route it straight through
 * `resolveGroupCollapsed`.
 */
export function flattenVisible<Row>(
  nodes: readonly GroupTreeNode<Row>[],
  isCollapsed: (pathKey: string, level: number, value: string) => boolean,
): GroupTreeNode<Row>[] {
  const out: GroupTreeNode<Row>[] = [];
  for (const node of nodes) {
    out.push(node);
    if (node.kind === 'group' && !isCollapsed(node.pathKey, node.level, node.value)) {
      out.push(...flattenVisible(node.children, isCollapsed));
    }
  }
  return out;
}

/**
 * Whether one group is collapsed — FlowTable's `isGroupCollapsed` rule,
 * lifted verbatim so LedgerTable cannot drift from it. Precedence:
 *  1. the rep's explicit per-group `overrides` (keyed by `pathKey`);
 *  2. `baseCollapsed` from an external Expand/Collapse-all
 *     `groupCollapseCommand` (`null` = no command in force);
 *  3. `defaultRule` (`config.defaultGroupCollapsed`) when the mount supplies one;
 *  4. otherwise the level-0 fallback: tracker grouping folds `Snoozed`.
 * The overrides/base are ephemeral component state the caller owns and
 * resets on `${activeViewId} ${levels.join(',')}` change.
 */
export function resolveGroupCollapsed({
  viewId, pathKey, level, value, overrides, baseCollapsed, defaultRule, levels,
}: {
  viewId: string;
  pathKey: string;
  level: number;
  value: string;
  overrides: ReadonlyMap<string, boolean>;
  baseCollapsed: boolean | null;
  defaultRule: FlowTableConfig<Record<string, unknown>>['defaultGroupCollapsed'];
  levels: readonly string[];
}): boolean {
  const override = overrides.get(pathKey);
  if (override !== undefined) return override;
  if (baseCollapsed !== null) return baseCollapsed;
  return defaultRule?.({ viewId, level, value })
    ?? (level === 0 && levels[0] === TRACKER_GROUP_KEY && value === 'Snoozed');
}
